import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import ActionsAPI from 'services/ActionsAPI'
import BreakdownTable from 'components/BreakdownTable'
import ReportPeriodPicker, { resolvePeriod } from 'components/ReportPeriodPicker'
import KpiReportButton from './KpiReportButton'
import PerformanceScopePicker from './PerformanceScopePicker'
import TaskOriginPicker from './TaskOriginPicker'
import { RAG } from 'features/dashboard/charts/scoreColour'
import InfoPopover, { PopItem, PopLead, PopList } from 'features/actions/workspace/InfoPopover'
import { raisedDates, remedialsViewUrl, resolvedDates } from 'features/actions/workspace/remedialsQuery'
import {
  COUNT_COLUMNS,
  NOT_ENOUGH_DATA,
  SITE_COLUMNS,
  SOURCE_FILTER_INFO,
  SOURCE_FILTER_OPTIONS,
  SOURCE_NAMES,
  notCountedSentence,
  isEmptyPerformance,
  performanceCards,
  performanceParams,
  periodSentence,
  scopeExtra,
  severityRows,
  sourceRows,
} from 'features/actions/workspace/remedialPerformance'
import CountDonut from 'features/dashboard/charts/CountDonut'
import ChartKeyExplainer from 'features/dashboard/charts/ChartKeyExplainer'
import MonthlyStackedBars from 'features/dashboard/charts/MonthlyStackedBars'
import HorizontalStackedBars from 'features/dashboard/charts/HorizontalStackedBars'
import {
  REMEDIAL_PROGRESS_BUCKETS,
  remedialBucketsFromRow,
  remedialBucketTotal,
  remedialBarRow,
} from 'features/actions/workspace/remedialProgressBuckets'

const MOBILE = '768px'

const MONTH_BUCKETS = REMEDIAL_PROGRESS_BUCKETS.filter(b => b.key !== 'dismissedAfterApproval')

const BUCKET_VIEW = {
  openOnTrack: 'all_open',
  overdue: 'overdue',
  resolvedLate: 'resolved',
  resolvedOnTime: 'resolved',
  resolvedNoDate: 'resolved',
  dismissedAfterApproval: 'dismissed',
}

const ColumnsExplainer = ({ title, columns }) => (
  <InfoPopover label={`What the columns in ${title.toLowerCase()} mean`} title={`${title}: what the columns mean`}>
    <PopList>
      {columns.map(column => (
        <PopItem key={column.key}><span><strong>{column.label}</strong>: {column.title}</span></PopItem>
      ))}
    </PopList>
  </InfoPopover>
)

const RemedialPerformancePanel = ({ onNavigate, scope, period, onScopeChange, onPeriodChange }) => {
  const [source, setSource] = useState(null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const latest = useRef(0)
  const range = useMemo(() => resolvePeriod(period), [period])
  const params = useMemo(() => performanceParams(scope, range, source), [scope, range, source])

  const load = useCallback(() => {
    latest.current += 1
    const id = latest.current
    setLoading(true)
    setError(false)
    ActionsAPI.performance(params)
      .then(res => {
        if (latest.current !== id) return
        setData(res?.data?.data || null)
        setLoading(false)
      })
      .catch(() => {
        if (latest.current !== id) return
        setError(true)
        setLoading(false)
      })
  }, [params])

  useEffect(() => { load() }, [load])

  const extra = scopeExtra(scope)
  const go = (view, more = {}, { raisedInPeriod = false } = {}) => {
    const dates = raisedInPeriod ? raisedDates(range) : resolvedDates(view, range)
    if (onNavigate) onNavigate(remedialsViewUrl({}, view, { ...extra, ...(source && view !== 'awaiting_review' ? { source } : {}), ...more, ...dates }))
  }
  const firstPaint = loading && !data && !error
  const empty = !loading && !error && isEmptyPerformance(data)
  const cards = performanceCards(data?.totals)
  const multiSite = scope?.type !== 'bp'
  const sites = data?.byBuilding || []

  return (
    <Panel aria-labelledby='remedial-performance-title' aria-busy={firstPaint}>
      <Head>
        <div>
          <Title id='remedial-performance-title'>Remedial performance</Title>
          <Subtitle>How many remedials were raised and put right, and how many were put right on time. The sites and dates here are shared with Task performance above, so changing them in one place changes both.</Subtitle>
        </div>
        <HeadActions>
          <KpiReportButton initial={{ scope, period, origin: null }} title='Download a KPI report with the sites and dates shown here. It includes these remedial figures.' />
          {onScopeChange && <PerformanceScopePicker scope={scope} onChange={onScopeChange} />}
        </HeadActions>
      </Head>
      <FilterRow>
        {onPeriodChange && <ReportPeriodPicker value={period} onChange={onPeriodChange} label='Raised or resolved' />}
        <TaskOriginPicker
          value={source}
          onChange={setSource}
          label='Source'
          options={SOURCE_FILTER_OPTIONS}
          infoTitle='Where do remedials come from?'
          infoItems={SOURCE_FILTER_INFO}
          infoNote={null}
        />
      </FilterRow>
      <PeriodNote>{periodSentence(range)} {source ? `Showing remedials ${SOURCE_NAMES[source].toLowerCase()} only.` : 'Showing remedials from every source.'}</PeriodNote>

      {error ? (
        <ErrorBox role='alert'>
          <span>We could not load remedial performance.</span>
          <RetryBtn type='button' onClick={load}>Try again</RetryBtn>
        </ErrorBox>
      ) : (
        <>
          <Cards>
            {cards.map(card => {
              const colour = card.level ? RAG[card.level].fg : undefined
              const clickable = !!(card.view && onNavigate) && !firstPaint
              return (
                <CardTile key={card.key}>
                  <CardTop>
                    <CardLabel>{card.label}</CardLabel>
                    <InfoPopover label={`What does ${card.label.toLowerCase()} mean?`} title={card.label}>
                      <PopLead>{card.explain}</PopLead>
                    </InfoPopover>
                  </CardTop>
                  {firstPaint && <SkeletonValue />}
                  {!firstPaint && clickable && (
                    <CardLink type='button' onClick={() => go(card.view)} aria-label={`${card.label}: ${card.value}. Open the list`}>
                      <CardValue $muted={card.value === NOT_ENOUGH_DATA} style={{ color: colour }}>{card.value}</CardValue>
                      <CardLinkText>See these</CardLinkText>
                    </CardLink>
                  )}
                  {!firstPaint && !clickable && <CardValue $muted={card.value === NOT_ENOUGH_DATA} style={{ color: colour }}>{card.value}</CardValue>}
                </CardTile>
              )
            })}
          </Cards>

          {!firstPaint && notCountedSentence(data?.totals) && (
            <NotCounted role='note'>
              <span>{notCountedSentence(data?.totals)}</span>
              {onNavigate && (data?.totals?.suggestedInReview || 0) > 0 && (
                <NotCountedLink type='button' onClick={() => go('awaiting_review')}>Review them</NotCountedLink>
              )}
            </NotCounted>
          )}

          {!firstPaint && !empty && data && remedialBucketTotal(data.totals) > 0 && (
            <ChartBlock $dim={loading}>
              <ChartTitle>
                Where the remedials stand
                <ChartKeyExplainer
                  title='Where the remedials stand'
                  lead='Every remedial raised between the dates you chose, counted once, by where it stands today. The total always matches Raised. Open counts the ones still being dealt with that are not yet past their due date, so Open and Overdue never double-count.'
                  buckets={REMEDIAL_PROGRESS_BUCKETS}
                  drillNote={onNavigate ? 'Click any colour to open that list of remedials.' : null}
                />
              </ChartTitle>
              <ChartNote>The {(data.totals?.opened ?? 0).toLocaleString('en-GB')} remedials raised in this period, by where each one stands today.{onNavigate ? ' Click any colour to see those remedials.' : ''}</ChartNote>
              <CountDonut
                segments={remedialBucketsFromRow(data.totals).map(s => ({ ...s, drill: s.key }))}
                centreLabel='Remedials'
                noun='remedials'
                onSegmentClick={onNavigate ? seg => go(BUCKET_VIEW[seg.key] || 'all_open', {}, { raisedInPeriod: true }) : undefined}
              />
            </ChartBlock>
          )}

          {!firstPaint && !empty && data?.byMonth?.length > 0 && (
            <ChartBlock $dim={loading}>
              <ChartTitle>
                Month by month
                <ChartKeyExplainer
                  title='Month by month'
                  lead="Each column is one month, by the remedial's own due date. Past months show what was put right, future months show what is still due."
                  buckets={MONTH_BUCKETS}
                />
              </ChartTitle>
              <ChartNote>What has been put right, and what is still due, by the remedial&apos;s own due date. This spans the months either side of today, whatever dates you picked above.</ChartNote>
              <MonthlyStackedBars months={data.byMonth} buckets={MONTH_BUCKETS} />
            </ChartBlock>
          )}

          {firstPaint && (
            <SkelRows aria-hidden='true'>
              {[0, 1, 2, 3].map(i => <SkelRow key={i} />)}
            </SkelRows>
          )}

          {empty && <EmptyNote>Nothing to show for these sites and dates. No remedials were raised or resolved, and none are open.</EmptyNote>}

          {!firstPaint && !empty && data && (
            <Tables $dim={loading}>
              <BreakdownTable
                title='By severity'
                titleExtra={<ColumnsExplainer title='By severity' columns={COUNT_COLUMNS} />}
                helpText='Each level is shown on its own. Click a level to see its open remedials.'
                rows={severityRows(data.bySeverity)}
                columns={COUNT_COLUMNS}
                nameLabel='Severity'
                rowKey={r => r.severity}
                defaultSort='rank'
                searchPlaceholder='Search severities…'
                csvName='remedials-by-severity'
                renderName={r => (
                  <LevelName>
                    <Dot style={{ background: RAG[r.severity].fg }} aria-hidden='true' />
                    {r.name}
                  </LevelName>
                )}
                onRowClick={(r, column) => go(column?.drill || 'all_open', { severity: r.severity })}
              />
              <BreakdownTable
                title='Where they came from'
                titleExtra={<ColumnsExplainer title='Where they came from' columns={COUNT_COLUMNS} />}
                helpText='Documents, incidents, completed task forms, or added by someone by hand. Click a row to see its open remedials.'
                rows={sourceRows(data.bySource)}
                columns={COUNT_COLUMNS}
                nameLabel='Source'
                rowKey={r => r.source}
                defaultSort='rank'
                searchPlaceholder='Search sources…'
                csvName='remedials-by-source'
                onRowClick={(r, column) => go(column?.drill || 'all_open', { source: r.source })}
              />
              {multiSite && sites.length > 0 && (
                <ChartBlock>
                  <ChartTitle>
                    By site
                    <ChartKeyExplainer
                      title='By site'
                      lead="One bar per building, busiest first. The bar shows how that building's remedials split."
                      buckets={REMEDIAL_PROGRESS_BUCKETS}
                      drillNote={onNavigate ? "Click a bar to open that building's open remedials." : null}
                    />
                  </ChartTitle>
                  <ChartNote>The busiest sites first. Each bar shows how that site's remedials are split.</ChartNote>
                  <HorizontalStackedBars
                    rows={sites.map(remedialBarRow)}
                    buckets={REMEDIAL_PROGRESS_BUCKETS}
                    onRowClick={onNavigate
                      ? r => onNavigate(remedialsViewUrl({}, 'all_open', { bp: r.bpId, ...resolvedDates('all_open', range) }))
                      : undefined}
                  />
                </ChartBlock>
              )}
              {multiSite && (
                <BreakdownTable
                  title={`By site (${sites.length})`}
                  titleExtra={<ColumnsExplainer title='By site' columns={SITE_COLUMNS} />}
                  helpText='Click a site to see its open remedials.'
                  rows={sites}
                  columns={SITE_COLUMNS}
                  nameLabel='Site'
                  rowKey={r => r.bpId}
                  defaultSort='overdue'
                  searchPlaceholder='Search sites…'
                  csvName='remedials-by-site'
                  emptyText='No remedials at any site for these dates.'
                  onRowClick={(r, column) => {
                    const view = column?.drill || 'all_open'
                    if (onNavigate) onNavigate(remedialsViewUrl({}, view, { bp: r.bpId, ...resolvedDates(view, range) }))
                  }}
                />
              )}
            </Tables>
          )}

          <Legend>
            <strong>How these are worked out:</strong> raised counts every remedial created between the dates you chose, from any source, including ones still awaiting review and ones later dismissed. Resolved counts remedials put right between those dates. A remedial is on time when it was resolved on or before the due date it had that day; once a task was handling it, that is the latest deadline of its unfinished tasks. The on-time rate is on-time remedials divided by all resolved remedials that had a due date. Overdue now and open now are as things stand today, whatever dates you chose.
          </Legend>
        </>
      )}
    </Panel>
  )
}

export default RemedialPerformancePanel

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.9; }
`

const Panel = styled.section`
  background: #fff;
  border: 1px solid #ececf1;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  @media (max-width: ${MOBILE}) { padding: 14px; }
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: #101828;
`

const Subtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: #475467;
  max-width: 720px;
`

const HeadActions = styled.div`
  display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end;

  @media (max-width: ${MOBILE}) { width: 100%; justify-content: flex-start; }
`

const FilterRow = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 12px;
`

const NotCounted = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px;
  margin-top: 12px; padding: 10px 14px; border-radius: 10px;
  background: #f7f8fc; border: 1px solid #e4e6ee;
  font-size: 14px; line-height: 1.45; color: #344054;
`

const NotCountedLink = styled.button`
  font: inherit; font-size: 14px; font-weight: 700; color: #1d7f8a;
  min-height: 40px; padding: 0 12px; border-radius: 8px; cursor: pointer;
  background: #fff; border: 1px solid #33becc;
  &:hover { background: #e6f7f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const PeriodNote = styled.p`
  margin: 0;
  font-size: 15px;
  color: #475467;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 1100px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: ${MOBILE}) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`

const CardTile = styled.div`
  border: 1px solid #ececf1;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`

const CardLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #344054;
`

const CardValue = styled.span`
  display: block;
  font-size: 26px;
  font-weight: 800;
  color: #101828;
  font-variant-numeric: tabular-nums;
  ${p => p.$muted && `font-size: 15px; font-weight: 600; color: #6b7280; line-height: 1.3;`}
`

const CardLink = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-height: 40px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  &:hover span:last-child { text-decoration: underline; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; border-radius: 6px; }
`

const CardLinkText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #17727b;
`

const SkeletonValue = styled.span`
  display: block;
  width: 48px;
  height: 26px;
  border-radius: 6px;
  background: #eef0f5;
  animation: ${pulse} 1.3s ease-in-out infinite;
`

const SkelRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkelRow = styled.div`
  height: 36px;
  border-radius: 8px;
  background: #eef0f5;
  animation: ${pulse} 1.3s ease-in-out infinite;
`

const ChartBlock = styled.div`
  border: 1px solid #eceef3; border-radius: 12px; padding: 14px 16px 16px; margin-top: 16px; min-width: 0;
  transition: opacity 160ms ease; opacity: ${({ $dim }) => ($dim ? 0.55 : 1)};
  @media (max-width: 768px) { padding: 12px 12px 14px; }
`
const ChartTitle = styled.div`
  font-size: 12px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #475467;
  display: inline-flex; align-items: center; gap: 6px;
`
const ChartNote = styled.div`
  font-size: 12px; color: #667085; margin: 3px 0 12px; line-height: 1.45;
`
const Tables = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  opacity: ${({ $dim }) => ($dim ? 0.6 : 1)};
  transition: opacity 0.15s ease;
`

const LevelName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
`

const EmptyNote = styled.p`
  margin: 0;
  padding: 14px;
  border-radius: 10px;
  background: #f8fafc;
  font-size: 15px;
  color: #475467;
`

const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px;
  border-radius: 10px;
  background: ${RAG.critical.bg};
  color: ${RAG.critical.fg};
  font-size: 15px;
`

const RetryBtn = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${RAG.critical.fg};
  background: #fff;
  color: ${RAG.critical.fg};
  font-weight: 700;
  cursor: pointer;
`

const Legend = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #475467;
`
